import type {Route} from 'vue-router';
import type {
  KeywordsToInsertItem,
  LaunchKeywordsV2Form,
  PostKeywordsV2RequestData,
  Website,
} from '@/models';
export {languageCountryCode} from './constants';
export {Filter} from './Filter';
export {Pagination} from './Pagination';
export {Sorting} from './Sorting';

export type Primitive = null | undefined | string | number | boolean | symbol | bigint

export type DeepPartialNullable<T> = T extends object ? {
  [P in keyof T]?: DeepPartialNullable<T[P]> | null
} : T | null

export function getErrorMessage(e: any) {
  return e?.response?.data?.message ?? e?.message ?? 'Unknown error';
}

export function getIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

export function noop() {
  // No operation performed.
}

export function uniq<T>(array: T[]) {
  return Array.from(new Set(array));
}

export function* range(start: number, end: number, step: number = 1) {
  for (let i = start; i < end; i += step) yield i;
}

export function getSites(websites: Website[]) {
  const sites: string[] = [];

  const hiddenFeeds = ['cbs_tt', 'iac', 'iac_rd'];
  for (const website of websites) {
    if (hiddenFeeds.includes(website.feed)) continue;
    if (Array.isArray(website.adunits)) {
      for (const adunit of website.adunits) {
        if (adunit.traffic_source === 'facebook') {
          sites.push(`${website.domain} - ${adunit.adUnitId}`);
        }
      }
    } else {
      sites.push(website.domain);
    }
  }
  sites.sort();

  return sites;
}

export function getFeeds(websites: Website[], sites: string[]) {
  // 1. get websites that are in the sites array
  websites = websites
    .filter(website => sites.some(site => site.startsWith(website.domain)));
  // 2. get unique feeds
  return websites
    .map(el => el.feed)
    .filter((value, index, array) => array.indexOf(value) === index).sort();
}

export function getCustomTag() {
  let customTag = '';

  const email = localStorage.getItem('email');
  if (email) [customTag] = email.split('@');

  return customTag;
}

export function splitKeywords(keywords: string) {
  return keywords.split('\n')
    .map(el => el.trim())
    .filter(Boolean);
}

export function countNonEmptyLines(value: string) {
  return value.split('\n')
    .map(el => el.trim())
    .filter(Boolean)
    .length;
}

export function parseKeywordsToInsertInput(value: string, maxLines: number = 5000) {
  const items: KeywordsToInsertItem[] = [];
  const lines = value.split('\n');

  for (const [index, rawLine] of lines.entries()) {
    const line = rawLine.trim();
    if (!line) continue;

    if (items.length >= maxLines) {
      return {
        items: [] as KeywordsToInsertItem[],
        errorMessage: `You can add up to ${maxLines} keywords at a time.`,
      };
    }

    const parts = line.split(/[;,]/).map(el => el.trim());
    if (parts.length !== 2) {
      return {
        items: [] as KeywordsToInsertItem[],
        errorMessage: `Line ${index + 1} must be in the format keyword,market or keyword;market.`,
      };
    }

    const [keyword, market] = parts;
    if (!keyword || !market) {
      return {
        items: [] as KeywordsToInsertItem[],
        errorMessage: `Line ${index + 1} must include both keyword and market.`,
      };
    }

    if (!/^[a-zA-Z]{2}$/.test(market)) {
      return {
        items: [] as KeywordsToInsertItem[],
        errorMessage: `Line ${index + 1} market must be exactly 2 letters.`,
      };
    }

    items.push({
      keyword,
      market: market.toUpperCase(),
    });
  }

  return {
    items,
    errorMessage: null as string | null,
  };
}

export function splitKeywordsWithMarkets(keywords: string) {
  return keywords.split('\n')
    .filter(el => el.includes(','))
    .map(el => el.split(','))
    .map(([keyword, market]) => [keyword?.trim(), market?.trim().toUpperCase()])
    .filter(([keyword, market]) => Boolean(keyword) && Boolean(market));
}

export function splitKeywordsWithMarketsBidAmount(keywords: string) {
  return keywords.split('\n')
    .filter(el => el.includes(','))
    .map(el => el.split(','))
    .map(([keyword, market, bidAmount]) => [keyword?.trim(), market?.trim().toUpperCase(), bidAmount?.trim()])
    .filter(([keyword, market, bidAmount]) => Boolean(keyword) && Boolean(market) && Boolean(bidAmount));
}

export function splitSite(site: string) {
  return site.split(' - ');
}

export function isString(value: unknown) {
  return typeof value === 'string';
}

export function isUndefined(value: unknown) {
  return value === undefined;
}

export function isNull(value: unknown) {
  return value === null;
}

export function isEmptyString(value: string) {
  return value === '';
}

export function isFalsy(value: unknown) {
  return isUndefined(value) || isNull(value) || isEmptyString(value as string);
}

export function isNil(value: any): value is null | undefined {
  return value === null || value === undefined;
}

export function isMinLength<T extends {length: number}>(value: T, min: number) {
  return value.length >= min;
}

export function isLaunchKeywordsV2FormValid(form: LaunchKeywordsV2Form) {
  const {accountId, adStyles, adsetNo, bidAmount, bidStrategy, budget, customTag, duplicateFromArchive, environment, keywordId, keywords, markets, sites, startDate} = form;
  const results = [{
    param: 'accountId',
    // optional, integer string
    isValid: isString(accountId) || /^\d+$/.test(accountId),
  }, {
    param: 'adStyles',
    // at least 1 true value
    isValid: Object.values(adStyles).includes(true),
  }, {
    param: 'adsetNo',
    isValid: isString(adsetNo) && isMinLength(adsetNo.trim(), 1),
  }, {
    param: 'bidAmount',
    // optional, decimal string
    isValid: keywords && countColumns(keywords) >= 3
      ? keywords.split('\n').map(el => el.split(',')).every(el => el[2] && /^\d+(\.\d+)?$/.test(el[2].trim()))
      : isFalsy(bidAmount) || (isString(bidAmount) && /^\d+(\.\d+)?$/.test(bidAmount)),
  }, {
    param: 'bidStrategy',
    isValid: isString(bidStrategy) && ['COST_CAP', 'LOWEST_COST_WITHOUT_CAP'].includes(bidStrategy),
  }, {
    param: 'budget',
    // integer string
    isValid: isString(budget) && /^\d+$/.test(budget),
  }, {
    param: 'customTag',
    // optional
    isValid: isFalsy(customTag) || isString(customTag),
  }, {
    param: 'environment',
    // optional, at least 1 char long
    isValid: isString(environment) || isMinLength((environment as string).trim(), 1),
  }, {
    param: 'keywordId',
    // optional, integer string
    isValid: isFalsy(keywordId) || (isString(keywordId) && /^\d+$/.test(keywordId)),
  }, {
    param: 'keywords',
    // required if duplicateFromArchive === false, at least 1 char long
    isValid: duplicateFromArchive || (isString(keywords) && isMinLength(keywords.trim(), 1)),
  }, {
    param: 'market',
    // only Latin letters, 2 chars long
    isValid: keywords && countColumns(keywords) >= 2
      ? keywords.split('\n').map(el => el.split(',')).every(el => el[1] && /^[a-zA-Z]{2}$/.test(el[1].trim()))
      : Array.isArray(markets) && isMinLength(markets, 1) && markets.every(el => /^[a-zA-Z]{2}$/.test(el)),
  }, {
    param: 'sites',
    // at least 1 element
    // at least 1 char long
    isValid: Array.isArray(sites) && isMinLength(sites, 1) && sites.every(el => isMinLength(el, 1)),
  }, {
    param: 'startDate',
    // ISO date string
    isValid: isString(startDate) && /^\d{4}-\d{2}-\d{2}$/.test(startDate),
  }, {
    param: 'accountId & environment',
    // one of the two
    isValid: (/^\d+$/.test(accountId) && isFalsy(environment)) || (isFalsy(accountId) && isMinLength(environment.trim(), 1)),
  }];
  return results.every(el => el.isValid);
}

export function getPostKeywordsV2RequestData(form: LaunchKeywordsV2Form) {
  const data: DeepPartialNullable<PostKeywordsV2RequestData>[] = [];

  const {accountId, adStyles, adsetNo, bidAmount, bidStrategy, budget, customTag, duplicateFromArchive, environment, keywordId, keywords, markets, sites, startDate} = form;
  const user = localStorage.getItem('email');
  for (const keyword of splitKeywords(duplicateFromArchive ? '{duplicate-from-archive}' : keywords)) {
    for (const market of markets) {
      for (const site of sites) {
        const [domain, adUnitId] = splitSite(site);
        for (const adStyle in adStyles) {
          if (adStyles[adStyle]) {
            data.push({
              accountId,
              adStyle,
              adsetNo,
              adUnitId,
              bidAmount,
              bidStrategy,
              budget,
              customTag,
              domain,
              environment,
              keyword,
              keywordId,
              market,
              startDate,
              user,
            });
          }
        }
      }
    }
  }

  return data;
}

export function getPostKeywordsV2RequestDataUsingMarketsFromKeywords(form: LaunchKeywordsV2Form) {
  const data: DeepPartialNullable<PostKeywordsV2RequestData>[] = [];

  const {accountId, adStyles, adsetNo, bidAmount, bidStrategy, budget, customTag, environment, keywordId, keywords, sites, startDate} = form;
  const user = localStorage.getItem('email');
  for (const [keyword, market] of splitKeywordsWithMarkets(keywords)) {
    for (const site of sites) {
      const [domain, adUnitId] = splitSite(site);
      for (const adStyle in adStyles) {
        if (adStyles[adStyle]) {
          data.push({
            accountId,
            adStyle,
            adsetNo,
            adUnitId,
            bidAmount,
            bidStrategy,
            budget,
            customTag,
            domain,
            environment,
            keyword,
            keywordId,
            market,
            startDate,
            user,
          });
        }
      }
    }
  }

  return data;
}

export function getPostKeywordsV2RequestDataUsingMarketsBidAmountFromKeywords(form: LaunchKeywordsV2Form) {
  const data: DeepPartialNullable<PostKeywordsV2RequestData>[] = [];

  const {accountId, adStyles, adsetNo, bidStrategy, budget, customTag, environment, keywordId, keywords, sites, startDate} = form;
  const user = localStorage.getItem('email');
  for (const [keyword, market, bidAmount] of splitKeywordsWithMarketsBidAmount(keywords)) {
    for (const site of sites) {
      const [domain, adUnitId] = splitSite(site);
      for (const adStyle in adStyles) {
        if (adStyles[adStyle]) {
          data.push({
            accountId,
            adStyle,
            adsetNo,
            adUnitId,
            bidAmount,
            bidStrategy,
            budget,
            customTag,
            domain,
            environment,
            keyword,
            keywordId,
            market,
            startDate,
            user,
          });
        }
      }
    }
  }

  return data;
}

export function removeFalsyValues(obj: {[p: string]: unknown}) {
  const entries = Object.entries(obj)
    .filter(([, value]) => value !== undefined)
    .filter(([, value]) => value !== null)
    .filter(([, value]) => value !== '');
  return Object.fromEntries(entries);
}

export function logout() {
  localStorage.removeItem('logged');
  localStorage.removeItem('access-token');
  localStorage.removeItem('email');
  localStorage.removeItem('name');
}

export function countColumns(str: string) {
  const [line] = str.split('\n');
  return line.split(',').length;
}

export function getReturnUrl(route: Route) {
  return `${route.path}?${new URLSearchParams(route.query as Record<string, string>)}`;
}

export function isValidJsonString(jsonString: string): boolean {
  try {
    return Boolean(JSON.parse(jsonString));
  } catch (e) {
    return false;
  }
}

export function isValidBase64String(base64String: string): boolean {
  try {
    return Boolean(atob(base64String));
  } catch (e) {
    return false;
  }
}

export function isTokenExpired(token: string, offsetSeconds: number = 0): boolean {
  const [_headerBase64, payloadBase64, _signatureBase64] = token.split('.');
  if (!isValidBase64String(payloadBase64)) return true;
  const payloadString = atob(payloadBase64);
  if (!isValidJsonString(payloadString)) return true;
  const payload = JSON.parse(payloadString) as {exp: number};
  return Math.floor(Date.now() / 1000) >= payload.exp - offsetSeconds;
}

export function str(v: unknown): string {
  if (typeof v === 'string') {
    return v;
  }
  if (Array.isArray(v) && typeof v[0] === 'string') {
    return v[0];
  }
  return '';
}

export function int(v: unknown): number {
  return parseInt(str(v));
}

export class SearchParams {
  static createURLSearchParams(query: Route['query']) {
    const init: string[][] = [];
    for (const [key, value] of Object.entries(query)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item) {
            init.push([key, item]);
          }
        }
      } else if (value) {
        init.push([key, value]);
      }
    }
    return new URLSearchParams(init);
  }

  static updateFilter(filter: Record<string, string>, query: Route['query']) {
    for (const key in filter) {
      filter[key] = str(query[key]);
    }
  }

  static updatePagination(pagination: {page: number, perPage: number}, query: Route['query']) {
    const page = int(query.page);
    if (page) {
      pagination.page = page;
    }
    const perPage = int(query.perPage);
    if (perPage) {
      pagination.perPage = perPage;
    }
  }

  static mergeQueryParams(values: Record<string, any>, query: Route['query']) {
    query = {...query};
    for (const [key, value] of Object.entries(values)) {
      if (value === null || value === undefined || value === '') {
        delete query[key];
      } else {
        query[key] = value;
      }
    }
    return query;
  }
}

export function getScrollbarWidth(): number {
  return window.innerWidth - document.documentElement.clientWidth;
}
